// apps/client/src/components/contacts/ContactTimeline.tsx
//
// Shared contact timeline + composer. Used by every report (Discharges, Acquired
// Books, Settlement Age, Trail Drop-Off, Arrears, Refinance Intelligence) so a
// client's contact history follows them across reports. Mirrors the timeline +
// composer UX from docs/mockups/discharges-page-redesign.html.
//
// Presentational + local edit state only. Persistence is delegated to the passed
// `onSave` callback (the canonical write path lives in useLoanContacts). New
// entries are tagged with `sourcePage` (the report this component is mounted on).
//
// Channel/outcome vocabulary + icon colors mirror the existing Contact Activity
// viewer: phone=violet, email=amber, sms=blue, meeting=green.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Phone,
  Mail,
  MessageSquare,
  Users,
  Circle,
  Link2,
  Plus,
  Pencil,
  Trash2,
  Save,
} from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import {
  CONTACT_CHANNELS,
  CONTACT_OUTCOMES,
  ContactChannel,
  ContactOutcome,
  ContactSourcePage,
  LoanContactEntry,
} from '../../hooks/useLoanContacts';
import type { MutateContactInput } from '../../hooks/useLoanContacts';
import { canEditEntry } from '../../utils/contacts/loanContactMerge';
import DatePicker from '../common/DatePicker';
import { useLoanContactCapability } from '../../hooks/useLoanContactCapability';
import { useAuth } from '../../hooks/useAuth';
import { BROKER_FILTERED_ADMIN_COPY } from '../../utils/brokerFilteredAdmin';

// ---------------------------------------------------------------------------
// Public contract (Wave-2 consumer agents code against these — do not deviate)
// ---------------------------------------------------------------------------

export interface ContactTimelineProps {
  companyId: string;
  loanId: string;
  customerName?: string;
  loanBroker?: string; // borrower's broker (doc meta)
  sourcePage: ContactSourcePage; // tags entries CREATED here
  entries: LoanContactEntry[]; // pass contactsByLoan[loanId] || []
  availableBrokers: { id: string; name: string }[];
  currentUser: { uid?: string; displayName?: string; email?: string; role?: string };
  // Audit-grade write path. ContactTimeline knows the intent (create/update/
  // delete) directly, so it calls the operation explicitly — no array diffing.
  onMutate: (input: MutateContactInput) => Promise<{ ok: boolean; entryId: string }>;
  isSaving?: boolean;
  /**
   * OPTIONAL. Told `true` while a composer holds unsaved input (a new note
   * with content, or an edit that differs from the stored entry), and `false`
   * once it is saved, cancelled or the timeline unmounts. For hosts that can
   * unmount the timeline as a side effect of another action and must not
   * discard a half-written note. Omit it and nothing changes.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const CHANNEL_ICON: Record<ContactChannel, React.ComponentType<{ className?: string }>> = {
  phone: Phone,
  email: Mail,
  sms: MessageSquare,
  meeting: Users,
};

const CHANNEL_ICON_COLOR: Record<ContactChannel, string> = {
  phone: 'text-violet-400',
  email: 'text-amber-400',
  sms: 'text-blue-400',
  meeting: 'text-green-400',
};

const OUTCOME_CHIP_CLASS: Record<ContactOutcome, string> = {
  reached: 'bg-green-500/15 text-green-400 border-green-500/30',
  left_message: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  no_answer: 'bg-red-500/15 text-red-400 border-red-500/30',
  emailed: 'bg-gray-500/20 text-gray-300 border-gray-500/40',
  other: 'bg-gray-500/20 text-gray-300 border-gray-500/40',
};

const SOURCE_TAG_CLASS: Record<ContactSourcePage, string> = {
  Discharges: 'bg-red-500/15 text-red-400 border-red-500/30',
  'Acquired Books': 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  'Settlement Age': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'Trail Drop-Off': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  Arrears: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'Refinance Intelligence': 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  Clawbacks: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
};

// Written by services/admin-functions/scripts/backfill-loan-contacts.ts for rows
// migrated (June 2026) out of the four legacy per-page collections. Those rows
// genuinely have no human author, so the sentinel is an internal marker only and
// must never be rendered to a customer.
const MIGRATION_AUTHOR_SENTINEL = 'System (migrated)';

/** A human author name, or null when there is none worth showing. */
function humanAuthor(name: string | null | undefined): string | null {
  const trimmed = (name || '').trim();
  if (!trimmed) return null;
  if (trimmed === MIGRATION_AUTHOR_SENTINEL) return null;
  return trimmed;
}

/**
 * Attribution line for a timeline row.
 *
 * `broker` is who the contact was MADE by (user-selected, may be blank on legacy
 * rows); `createdBy` is who LOGGED it (always server-stamped on new entries). An
 * admin logging on behalf of a broker is supported, so when both exist and differ
 * we show both rather than picking one.
 */
export function contactAttributionLabel(entry: {
  broker?: string | null;
  createdBy?: string | null;
}): string {
  const madeBy = humanAuthor(entry.broker);
  const loggedBy = humanAuthor(entry.createdBy);
  if (madeBy && loggedBy && madeBy !== loggedBy) return `Made by ${madeBy} · logged by ${loggedBy}`;
  if (madeBy) return `Made by ${madeBy}`;
  if (loggedBy) return `Logged by ${loggedBy}`;
  return 'Author not recorded';
}

function todayYMD(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDisplayDate(ymd: string | null): string {
  if (!ymd) return 'No date';
  // ymd is 'YYYY-MM-DD' — parse as local to avoid TZ shift.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  if (!m) return ymd;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function sortableTime(entry: LoanContactEntry): number {
  if (entry.date) {
    const t = new Date(entry.date).getTime();
    if (!isNaN(t)) return t;
  }
  const c = entry.createdAt as any;
  if (c instanceof Timestamp) return c.toDate().getTime();
  if (typeof c?.toDate === 'function') return c.toDate().getTime();
  if (typeof c?.seconds === 'number') return c.seconds * 1000;
  if (c instanceof Date) return c.getTime();
  if (typeof c === 'string') {
    const t = new Date(c).getTime();
    if (!isNaN(t)) return t;
  }
  return 0;
}

function generateContactId(): string {
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function ContactTimeline({
  companyId,
  loanId,
  customerName,
  loanBroker,
  sourcePage,
  entries,
  availableBrokers,
  currentUser,
  onMutate,
  isSaving = false,
  onDirtyChange,
}: ContactTimelineProps): JSX.Element {
  // `mutateLoanContact` does NOT opt in to allowBrokerFilteredAdmin, so a
  // broker-restricted admin is default-denied at the chokepoint. This is a
  // LOAN contact (distinct from Client Care's logClientContact, which DOES opt
  // in and stays enabled for her in-filter clients).
  const { isBrokerFilteredAdmin } = useAuth();
  const { canLogActivity, loading: capabilityLoading } =
    useLoanContactCapability(companyId, loanId);
  // Draft overlay: persisted entries + any in-progress new entry, keyed by id.
  const [drafts, setDrafts] = useState<Record<string, LoanContactEntry>>({});
  // Ids currently being edited (existing entry edit OR a brand-new composer).
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
  // Per-entry in-flight + error state, so a failed mutateContact surfaces inline
  // (rather than silently swallowing) and the row can't be double-submitted.
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [opErrors, setOpErrors] = useState<Record<string, string>>({});
  // Inline "Made by is required" validation, raised on save attempt and cleared
  // as soon as the field changes.
  const [madeByErrors, setMadeByErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // A reused component must never carry an editable draft or capability from
    // the previous loan into the next loan.
    setDrafts({});
    setEditingIds(new Set());
    setPendingIds(new Set());
    setOpErrors({});
    setMadeByErrors({});
  }, [companyId, loanId]);

  const brokerLocked =
    currentUser.role === 'broker' || currentUser.role === 'broker_assistant';
  const selfBrokerName = currentUser.displayName || currentUser.email || '';

  // Combine persisted entries + draft entries (drafts override, new ids added).
  const combinedEntries = useMemo(() => {
    const byId: Record<string, LoanContactEntry> = {};
    for (const e of entries) byId[e.id] = e;
    for (const [id, e] of Object.entries(drafts)) byId[id] = e;
    return Object.values(byId).sort((a, b) => sortableTime(b) - sortableTime(a));
  }, [entries, drafts]);

  const count = combinedEntries.length;

  // Ids of persisted (server-stored) entries — distinguishes an edit (update)
  // from a brand-new composer (create), and gates which rows are mutable.
  const persistedById = useMemo(() => {
    const m: Record<string, LoanContactEntry> = {};
    for (const e of entries) m[e.id] = e;
    return m;
  }, [entries]);

  // Unsaved input in any open composer. A just-opened, untouched composer is
  // NOT dirty (its date and locked "Made by" are defaults, not user input), so
  // opening the form by accident does not block the host.
  const isDirty = useMemo(() => {
    for (const id of Array.from(editingIds)) {
      const draft = drafts[id];
      if (!draft) continue;
      const stored = persistedById[id];
      if (!stored) {
        if ((draft.comments || '').trim() || draft.channels.length > 0 || draft.outcome) return true;
        continue;
      }
      if (
        (draft.comments || '') !== (stored.comments || '') ||
        (draft.broker || '') !== (stored.broker || '') ||
        (draft.date || null) !== (stored.date || null) ||
        (draft.outcome || null) !== (stored.outcome || null) ||
        draft.channels.join('|') !== stored.channels.join('|')
      ) {
        return true;
      }
    }
    return false;
  }, [drafts, editingIds, persistedById]);

  // The callback is read through a ref so a host passing an inline function
  // does not re-fire the effect on every render.
  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty);
  }, [isDirty]);
  useEffect(
    // Unmounting discards the composer, so the host must stop treating it as dirty.
    () => () => onDirtyChangeRef.current?.(false),
    [],
  );

  const handleLogContact = () => {
    if (!canLogActivity) return;
    const id = generateContactId();
    const draft: LoanContactEntry = {
      id,
      channels: [],
      date: todayYMD(),
      broker: brokerLocked ? selfBrokerName : '',
      outcome: null,
      comments: '',
      sourcePage,
      createdAt: Timestamp.now(),
      createdBy: currentUser.displayName || currentUser.email || 'Unknown',
    };
    setDrafts((prev) => ({ ...prev, [id]: draft }));
    setEditingIds((prev) => new Set(prev).add(id));
  };

  const handleEdit = (entry: LoanContactEntry) => {
    setDrafts((prev) => ({ ...prev, [entry.id]: { ...entry } }));
    setEditingIds((prev) => new Set(prev).add(entry.id));
  };

  const updateDraftField = <K extends keyof LoanContactEntry>(
    id: string,
    field: K,
    value: LoanContactEntry[K]
  ) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const toggleChannel = (id: string, channel: ContactChannel) => {
    setDrafts((prev) => {
      const cur = prev[id];
      if (!cur) return prev;
      const channels = cur.channels.includes(channel)
        ? cur.channels.filter((c) => c !== channel)
        : [...cur.channels, channel];
      return { ...prev, [id]: { ...cur, channels } };
    });
  };

  const clearEditState = (id: string) => {
    setEditingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setOpErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setMadeByErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleCancel = (id: string) => {
    clearEditState(id);
  };

  const setPending = (id: string, on: boolean) =>
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const clearOpError = (id: string) =>
    setOpErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });

  const opErrorMessage = (e: unknown): string => {
    const m = (e as { message?: unknown })?.message;
    return typeof m === 'string' && m ? m : 'Something went wrong. Please try again.';
  };

  const handleSave = async (id: string) => {
    const draft = drafts[id];
    if (!canLogActivity || !draft || pendingIds.has(id)) return; // guard access + double-submit
    // "Made by" is mandatory. We deliberately do NOT default it to the current
    // user: silently attributing a call to the admin who typed it up is a wrong
    // attribution on a client file, which is worse than making them choose.
    if (!(draft.broker || '').trim()) {
      setMadeByErrors((prev) => ({
        ...prev,
        [id]: 'Select who this contact was made by before saving.',
      }));
      return;
    }
    // Editable fields only — the server stamps the actor + provenance.
    const editable = {
      channels: draft.channels,
      date: draft.date,
      broker: draft.broker,
      outcome: draft.outcome,
      comments: draft.comments,
    };
    setPending(id, true);
    clearOpError(id);
    try {
      if (persistedById[id]) {
        await onMutate({ operation: 'update', loanId, entryId: id, entry: editable });
      } else {
        // The draft id doubles as the idempotency key AND the stored id. A
        // retry of this create cannot append a second entry, and because the
        // saved row arrives under the SAME id the open composer simply
        // overlays it in combinedEntries: the composer and the saved row can
        // no longer both show while the callable is still resolving.
        await onMutate({
          operation: 'create',
          loanId,
          entry: { ...editable, sourcePage },
          customerName,
          loanBroker,
          clientEntryId: id,
        });
      }
      clearEditState(id);
    } catch (e) {
      // Surface the failure inline (keep the form open for retry).
      setOpErrors((prev) => ({ ...prev, [id]: opErrorMessage(e) }));
    } finally {
      setPending(id, false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!canLogActivity) return;
    // A never-saved draft just clears locally — nothing to delete server-side.
    if (!persistedById[id]) {
      clearEditState(id);
      return;
    }
    if (pendingIds.has(id)) return;
    setPending(id, true);
    clearOpError(id);
    try {
      await onMutate({ operation: 'delete', loanId, entryId: id });
      clearEditState(id);
    } catch (e) {
      setOpErrors((prev) => ({ ...prev, [id]: opErrorMessage(e) }));
    } finally {
      setPending(id, false);
    }
  };

  // ----- render helpers -----

  const renderTimelineRow = (entry: LoanContactEntry) => {
    if (editingIds.has(entry.id)) {
      return <div key={entry.id}>{renderComposer(entry.id)}</div>;
    }
    // Server is authoritative (mutateLoanContact enforces this); we only hide
    // controls the user can't use. Admins any; others own; legacy/Client-Care locked.
    const editable =
      canLogActivity && canEditEntry(currentUser.role, currentUser.uid, entry);
    return (
      <div
        key={entry.id}
        onClick={editable ? () => handleEdit(entry) : undefined}
        className={`flex gap-3 bg-gray-900/60 border border-gray-700 rounded-lg p-2.5 transition-colors ${
          editable ? 'cursor-pointer hover:border-gray-500 hover:bg-gray-700/25' : ''
        }`}
      >
        <div className="flex items-center gap-1 pt-0.5 min-w-[1.2rem]">
          {entry.channels.length > 0 ? (
            entry.channels.map((ch) => {
              const Icon = CHANNEL_ICON[ch] || Circle;
              return (
                <Icon
                  key={ch}
                  className={`w-4 h-4 ${CHANNEL_ICON_COLOR[ch] || 'text-gray-400'}`}
                />
              );
            })
          ) : (
            <span className="text-gray-500">—</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">
              {formatDisplayDate(entry.date)}
            </span>
            <span className="text-xs text-gray-400">{contactAttributionLabel(entry)}</span>
            {entry.outcome && (
              <span
                className={`text-[0.6rem] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                  OUTCOME_CHIP_CLASS[entry.outcome] || OUTCOME_CHIP_CLASS.other
                }`}
              >
                {CONTACT_OUTCOMES[entry.outcome] || entry.outcome}
              </span>
            )}
            <span
              className={`text-[0.6rem] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border whitespace-nowrap ${
                SOURCE_TAG_CLASS[entry.sourcePage] || SOURCE_TAG_CLASS.Discharges
              }`}
            >
              {entry.sourcePage}
            </span>
          </div>
          <div className="text-sm text-gray-400 mt-0.5 truncate">
            {entry.comments ? entry.comments : <span className="text-gray-500">No notes</span>}
          </div>
        </div>
        {editable && (
          <div className="flex gap-0.5 items-start">
            <button
              type="button"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(entry);
              }}
              className="text-gray-500 hover:text-white hover:bg-white/5 rounded p-1 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(entry.id);
              }}
              className="text-gray-500 hover:text-red-400 hover:bg-white/5 rounded p-1 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderComposer = (id: string) => {
    const draft = drafts[id];
    if (!draft) return null;
    // A value with no matching <option> renders as "Select broker…" while state
    // holds the name, so the UI would save something it never showed. Anyone not
    // in the company brokers list (e.g. a Client Success Manager, or a broker who
    // has since left but authored an older entry) gets a synthetic option.
    const knownBrokerNames = new Set(availableBrokers.map((b) => b.name));
    const syntheticNames = [selfBrokerName, draft.broker].filter(
      (n, i, arr): n is string =>
        !!n && !knownBrokerNames.has(n) && arr.indexOf(n) === i
    );
    const madeByError = madeByErrors[id];
    return (
      <div className="bg-gray-900/60 border border-dashed border-blue-400/40 rounded-xl p-3.5">
        <span className="block text-[0.62rem] uppercase tracking-wide text-gray-400 font-bold mb-1.5">
          Channels
        </span>
        <div className="flex gap-1.5 flex-wrap mb-3">
          {(Object.keys(CONTACT_CHANNELS) as ContactChannel[]).map((ch) => {
            const Icon = CHANNEL_ICON[ch];
            const on = draft.channels.includes(ch);
            return (
              <button
                key={ch}
                type="button"
                onClick={() => toggleChannel(id, ch)}
                className={`inline-flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1.5 rounded-full border transition-colors ${
                  on
                    ? 'bg-blue-600/20 border-blue-600/55 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {CONTACT_CHANNELS[ch].label}
              </button>
            );
          })}
        </div>

        <div className="flex gap-2.5 flex-wrap mb-2.5">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-[0.62rem] uppercase tracking-wide text-gray-400 font-bold mb-1.5">
              Date
            </label>
            <DatePicker
              id={`contact-date-${id}`}
              value={draft.date ?? undefined}
              onChange={(d) => updateDraftField(id, 'date', d ?? null)}
              placeholder="Select date"
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-[0.62rem] uppercase tracking-wide text-gray-400 font-bold mb-1.5">
              Made by
            </label>
            <select
              aria-label="Made by"
              value={draft.broker || ''}
              disabled={brokerLocked}
              onChange={(e) => {
                updateDraftField(id, 'broker', e.target.value);
                setMadeByErrors((prev) => {
                  if (!(id in prev)) return prev;
                  const next = { ...prev };
                  delete next[id];
                  return next;
                });
              }}
              className={`w-full bg-gray-700 border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed ${
                madeByError ? 'border-red-500' : 'border-gray-600'
              }`}
            >
              <option value="">Select broker…</option>
              {syntheticNames.map((n) => (
                <option key={`self-${n}`} value={n}>
                  {n}
                </option>
              ))}
              {availableBrokers.map((b) => (
                <option key={b.id} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
            {madeByError && (
              <div className="mt-1 text-xs text-red-400">{madeByError}</div>
            )}
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-[0.62rem] uppercase tracking-wide text-gray-400 font-bold mb-1.5">
              Outcome
            </label>
            <select
              value={draft.outcome || ''}
              onChange={(e) =>
                updateDraftField(
                  id,
                  'outcome',
                  (e.target.value || null) as ContactOutcome | null
                )
              }
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">—</option>
              {(Object.keys(CONTACT_OUTCOMES) as ContactOutcome[]).map((o) => (
                <option key={o} value={o}>
                  {CONTACT_OUTCOMES[o]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-2.5">
          <label className="block text-[0.62rem] uppercase tracking-wide text-gray-400 font-bold mb-1.5">
            Notes
          </label>
          <textarea
            value={draft.comments}
            onChange={(e) => updateDraftField(id, 'comments', e.target.value)}
            placeholder="Conversation notes, client situation, follow-up…"
            rows={3}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>

        {opErrors[id] && (
          <div className="mb-2.5 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
            {opErrors[id]}
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleSave(id)}
            disabled={isBrokerFilteredAdmin || isSaving || pendingIds.has(id)}
            title={isBrokerFilteredAdmin ? BROKER_FILTERED_ADMIN_COPY : undefined}
            className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Save className="w-3.5 h-3.5" />
            {isSaving || pendingIds.has(id) ? 'Saving…' : 'Save Contact'}
          </button>
          <button
            type="button"
            onClick={() => handleCancel(id)}
            className="inline-flex items-center gap-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg px-3 py-2 text-sm font-semibold transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-sm font-semibold text-white flex items-center gap-2">
          Contact History
          {count > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.2rem] h-5 px-1.5 bg-green-500/20 text-green-400 rounded-full text-[0.62rem] font-bold">
              {count}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={handleLogContact}
          disabled={isBrokerFilteredAdmin || !canLogActivity}
          title={
            isBrokerFilteredAdmin
              ? BROKER_FILTERED_ADMIN_COPY
              : capabilityLoading
                ? 'Checking contact access…'
                : !canLogActivity
                  ? 'Contact history is read-only for this loan'
                  : undefined
          }
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" />
          Log Contact
        </button>
      </div>

      {!capabilityLoading && !canLogActivity && (
        <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-2.5 py-2 mb-2.5">
          Contact history is read-only for this loan.
        </div>
      )}

      <div className="flex items-start gap-1.5 text-xs text-gray-400 bg-cyan-500/[0.08] border border-cyan-500/20 rounded-lg px-2.5 py-2 mb-2.5">
        <Link2 className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 mt-px" />
        <span className="min-w-0">
          Shared across all reports: this client's history follows them on Acquired Books,
          Settlement Age &amp; Trail Drop-Off.
        </span>
      </div>

      {count > 0 ? (
        <div className="max-h-[19rem] overflow-y-auto flex flex-col gap-1.5 pr-1">
          {combinedEntries.map((entry) => renderTimelineRow(entry))}
        </div>
      ) : (
        <div className="text-sm text-gray-500 italic py-1.5">
          {canLogActivity
            ? 'No contact attempts recorded yet. Click “Log Contact” to record an outreach.'
            : 'No contact attempts recorded yet.'}
        </div>
      )}
    </div>
  );
}
